/* Broker Manager — edit (anyone with PB Form) / delete (admin) customs brokers in the shared list. */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Save, Trash2, X } from "lucide-react";

const BrokerRow = ({ b, isAdmin, onChanged }) => {
  const [edit, setEdit] = useState(null);
  const save = async () => {
    try {
      const r = await axios.put(`${API}/pb-doc/brokers/${b.id}`, edit, { headers: getAuthHeader() });
      toast.success(`Broker saved${r.data.forms_updated ? ` · ${r.data.forms_updated} PB form(s) re-printed` : ""}`);
      setEdit(null); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async () => {
    if (!window.confirm(`Delete broker ${b.name}? PB forms using it will need a new broker.`)) return;
    try { await axios.delete(`${API}/pb-doc/brokers/${b.id}`, { headers: getAuthHeader() }); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  if (edit) return (
    <div className="grid grid-cols-[1.2fr_0.8fr_1.4fr_auto] items-center gap-1.5 border-b py-1.5" data-testid={`broker-edit-${b.id}`}>
      <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="h-8" data-testid="broker-edit-name" />
      <Input value={edit.licence_no} onChange={(e) => setEdit({ ...edit, licence_no: e.target.value })} className="h-8" data-testid="broker-edit-licence" />
      <Input value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} className="h-8" data-testid="broker-edit-address" />
      <span className="flex gap-1">
        <Button size="sm" className="h-8 w-8 p-0" disabled={!edit.name.trim() || !edit.licence_no.trim()} onClick={save} data-testid="broker-edit-save"><Save className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEdit(null)} data-testid="broker-edit-cancel"><X className="h-4 w-4" /></Button>
      </span>
    </div>);
  return (
    <div className="grid grid-cols-[1.2fr_0.8fr_1.4fr_auto] items-center gap-1.5 border-b py-1.5 text-sm" data-testid={`broker-row-${b.id}`}>
      <span className="font-semibold">{b.name}</span><span className="font-mono text-xs">{b.licence_no}</span>
      <span className="truncate text-xs text-slate-500" title={b.address}>{b.address || "—"}</span>
      <span className="flex gap-1">
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEdit({ name: b.name, licence_no: b.licence_no, address: b.address || "" })} data-testid={`broker-edit-btn-${b.id}`}><Pencil className="h-4 w-4" /></Button>
        {isAdmin && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600" onClick={del} data-testid={`broker-del-${b.id}`}><Trash2 className="h-4 w-4" /></Button>}
      </span>
    </div>
  );
};

export const BrokerManager = ({ open, onClose, brokers, isAdmin, onChanged }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-2xl" data-testid="broker-manager">
      <DialogHeader><DialogTitle className="text-base">Customs brokers</DialogTitle></DialogHeader>
      <div className="grid grid-cols-[1.2fr_0.8fr_1.4fr_auto] gap-1.5 border-b pb-1 text-[11px] font-semibold text-slate-500">
        <span>Name</span><span>Licence No.</span><span>Address</span><span className="w-16" />
      </div>
      <div className="max-h-[60vh] overflow-y-auto">
        {brokers.length === 0 ? <p className="py-6 text-center text-sm text-slate-400" data-testid="broker-empty">No brokers yet.</p>
          : brokers.map(b => <BrokerRow key={b.id} b={b} isAdmin={isAdmin} onChanged={onChanged} />)}
      </div>
    </DialogContent>
  </Dialog>
);
